import {
  ArrowLeftOutlined,
  BarChartOutlined,
  BranchesOutlined,
  CheckCircleFilled,
  ClockCircleOutlined,
  CloudDownloadOutlined,
  CodeOutlined,
  DeleteOutlined,
  ExportOutlined,
  FileDoneOutlined,
  GithubOutlined,
  ReloadOutlined,
  SafetyCertificateOutlined,
} from '@ant-design/icons';
import { Alert, Button, Select } from 'antd';
import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { consumerSalesAppId, demoApps } from './demoData';
import { clearPreviewSession, PreviewReviewState, PreviewSession, readPreviewReview, readPreviewSession, savePreviewSession } from './previewSession';
import { AppSectionTabs } from './AppSectionTabs';
import { clickCiApiUrl } from './apiUrl';

const { Option } = Select;
const apiUrl = clickCiApiUrl;

interface LiveGitMapping {
  repository: string;
  branch: string;
  lastSyncedCommit: string | null;
  lastSyncedAt: string | null;
}

interface LiveAppDetail {
  name?: string;
  attributes?: { modifiedDate?: string; ownerId?: string; spaceId?: string };
  mapping?: LiveGitMapping;
  syncSummary?: {
    status?: 'checking' | 'in_sync' | 'qlik_ahead' | 'git_ahead' | 'auto_merge' | 'conflict';
    baselineCommit?: string;
    gitHeadCommit?: string;
    qlikChangeCount?: number;
    gitChangeCount?: number;
    conflictCount?: number;
    comparedAt?: string;
  };
}

type CheckoutStepKey = 'git' | 'validate' | 'create' | 'restore' | 'verify' | 'cleanup';
type CheckoutStepStatus = 'pending' | 'running' | 'succeeded' | 'failed';

interface CheckoutRunStep {
  key: CheckoutStepKey;
  title: string;
  purpose: string;
  status: CheckoutStepStatus;
  detail: string;
}

interface CheckoutRun {
  id: string;
  status: 'running' | 'succeeded' | 'failed';
  revision: string;
  steps: CheckoutRunStep[];
  result?: {
    previewAppId: string;
    previewAppName: string;
  };
  error?: string;
}

const checkoutStepIcons: Record<CheckoutStepKey, React.ReactNode> = {
  git: <GithubOutlined />,
  validate: <CodeOutlined />,
  create: <CloudDownloadOutlined />,
  restore: <ReloadOutlined />,
  verify: <SafetyCertificateOutlined />,
  cleanup: <DeleteOutlined />,
};

const CheckoutExecutionRoadmap: React.FC<{ run: CheckoutRun }> = ({ run }) => (
  <aside className="review-roadmap checkout-execution-roadmap" aria-label="Checkout execution roadmap">
    <div className="roadmap-heading"><strong>Checkout execution</strong><span>{run.status === 'running' ? 'Server task running' : run.status === 'succeeded' ? 'Preview ready' : 'Stopped safely'}</span></div>
    <ol>
      {run.steps.map((step, index) => {
        const state = step.status === 'succeeded' ? 'complete' : step.status === 'failed' ? 'error' : step.status;
        return <li className={`roadmap-step is-${state}`} key={step.key}>
          <span className="roadmap-icon">{step.status === 'succeeded' ? <CheckCircleFilled className="roadmap-check" /> : checkoutStepIcons[step.key]}</span>
          <div className="roadmap-step-copy">
            <span className="roadmap-step-number">{index + 1}</span>
            <strong>{step.title}</strong>
            <span className="roadmap-status">{step.status === 'succeeded' ? 'Completed' : step.status === 'running' ? 'Running' : step.status === 'failed' ? 'Blocked' : 'Pending'}</span>
            <p>{step.purpose}</p>
            <small>{step.detail}</small>
            {step.status === 'running' && <span className="roadmap-live"><span /> Live server confirmation</span>}
          </div>
        </li>;
      })}
    </ol>
    <div className="roadmap-trust"><FileDoneOutlined /> The connected source App remains unchanged until a reviewed commit is created</div>
  </aside>
);

export const QlikAppDetailPage: React.FC = () => {
  const { appId } = useParams<{ appId: string }>();
  const location = useLocation();
  const app = demoApps.find(item => item.id === appId) || demoApps[0];
  const remediationQuery = new URLSearchParams(location.search);
  const remediationTestPlan = remediationQuery.get('testPlan');
  const remediationRun = remediationQuery.get('run');
  const remediationCase = remediationQuery.get('case');
  const remediationObject = remediationQuery.get('object');
  const remediationReleasePlan = remediationQuery.get('releasePlan');
  const [preview, setPreview] = useState<PreviewSession | undefined>(() => readPreviewSession(app.id));
  const [reviewApproval, setReviewApproval] = useState<PreviewReviewState | undefined>(() => readPreviewReview(app.id));
  const [checkingOut, setCheckingOut] = useState(false);
  const [checkoutRun, setCheckoutRun] = useState<CheckoutRun>();
  const [deletingPreview, setDeletingPreview] = useState(false);
  const [actionMessage, setActionMessage] = useState<{ type: 'success' | 'error'; text: string }>();
  const [liveApp, setLiveApp] = useState<LiveAppDetail>();
  const checkoutPollGeneration = useRef(0);

  useEffect(() => () => { checkoutPollGeneration.current += 1; }, []);

  useEffect(() => {
    if (app.id !== consumerSalesAppId) return;
    let active = true;
    let timer: number | undefined;
    const refresh = () => void fetch(`${apiUrl}/api/demo-ci/apps/${app.id}`)
      .then(response => response.ok ? response.json() : undefined)
      .then(payload => { if (active && payload?.result) setLiveApp(payload.result); })
      .catch(() => undefined)
      .finally(() => { if (active) timer = window.setTimeout(refresh, 5000); });
    refresh();
    return () => { active = false; window.clearTimeout(timer); };
  }, [app.id]);

  const checkOut = async () => {
    if (app.id !== consumerSalesAppId) return;
    const pollGeneration = ++checkoutPollGeneration.current;
    setCheckingOut(true); setCheckoutRun(undefined); setActionMessage(undefined);
    try {
      const response = await fetch(`${apiUrl}/api/demo-ci/apps/${app.id}/check-out-runs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ revision: checkoutRevision }),
      });
      const payload = await response.json();
      if (!response.ok || !payload?.result?.id) throw new Error(payload?.message ?? 'Preview creation failed');
      let run = payload.result as CheckoutRun;
      if (checkoutPollGeneration.current !== pollGeneration) return;
      setCheckoutRun(run);
      while (run.status === 'running') {
        if (checkoutPollGeneration.current !== pollGeneration) return;
        const statusResponse = await fetch(`${apiUrl}/api/demo-ci/check-out-runs/${run.id}`);
        const statusPayload = await statusResponse.json();
        if (!statusResponse.ok) throw new Error(statusPayload?.message ?? 'Checkout status is unavailable');
        run = statusPayload.result as CheckoutRun;
        if (checkoutPollGeneration.current !== pollGeneration) return;
        setCheckoutRun(run);
        if (run.status === 'running') await new Promise(resolve => window.setTimeout(resolve, 1000));
      }
      if (run.status !== 'succeeded' || !run.result) throw new Error(run.error ?? 'Preview creation failed');
      const nextPreview: PreviewSession = {
        sourceAppId: app.id,
        previewAppId: run.result.previewAppId,
        previewAppName: run.result.previewAppName,
        createdAt: new Date().toISOString(),
      };
      clearPreviewSession(app.id);
      savePreviewSession(nextPreview);
      setPreview(nextPreview);
      setReviewApproval(undefined);
      setActionMessage({ type: 'success', text: `Preview App ${nextPreview.previewAppName} was restored from Git ${checkoutRevisionShort} in CICD Dev. The source App remains unchanged.` });
    } catch (error) {
      if (checkoutPollGeneration.current === pollGeneration) setActionMessage({ type: 'error', text: error instanceof Error ? error.message : 'Preview creation failed' });
    } finally {
      if (checkoutPollGeneration.current === pollGeneration) setCheckingOut(false);
    }
  };

  const deletePreview = async () => {
    if (!preview) return;
    setDeletingPreview(true); setActionMessage(undefined);
    try {
      const response = await fetch(`${apiUrl}/api/demo-ci/preview-apps/${preview.previewAppId}`, { method: 'DELETE' });
      const payload = await response.json();
      const alreadyMissing = response.status === 404 || /\b404\b/.test(payload?.message ?? '');
      if (!response.ok && !alreadyMissing) throw new Error(payload?.message ?? 'Preview cleanup failed');
      clearPreviewSession(app.id);
      setPreview(undefined);
      setReviewApproval(undefined);
      setActionMessage({ type: 'success', text: alreadyMissing ? 'The expired Preview record was cleared.' : 'Preview App was deleted. The source app was not changed.' });
    } catch (error) {
      setActionMessage({ type: 'error', text: error instanceof Error ? error.message : 'Preview cleanup failed' });
    } finally {
      setDeletingPreview(false);
    }
  };

  const commitPath = `/apps/${app.id}/commit${preview ? `?previewAppId=${preview.previewAppId}` : ''}`;
  const reviewTargetId = preview?.previewAppId ?? app.id;
  const reviewComplete = Boolean(
    reviewApproval?.previewAppId === reviewTargetId &&
    reviewApproval.completedAt
  );
  const activeSyncStatus = reviewApproval?.previewAppId === reviewTargetId
    ? reviewApproval.syncStatus
    : undefined;
  const liveMapping = liveApp?.mapping;
  const syncSummary = liveApp?.syncSummary;
  const liveStateLoading = app.id === consumerSalesAppId && !liveApp;
  const currentStatus = syncSummary?.status;
  const hasCommitChanges = currentStatus === 'qlik_ahead' || currentStatus === 'auto_merge' || currentStatus === 'conflict' || Boolean(preview);
  const mappedRepository = liveMapping?.repository ?? app.repository;
  const mappedBranch = liveMapping?.branch ?? app.branch;
  const mappedCommit = liveMapping?.lastSyncedCommit ?? '930969310d4ae6c3ea2d81869e5ceef135c02968';
  const mappedCommitShort = mappedCommit.slice(0, 7);
  const gitHeadCommit = syncSummary?.gitHeadCommit ?? mappedCommit;
  const gitHeadCommitShort = gitHeadCommit.slice(0, 7);
  const checkoutRevision = currentStatus === 'git_ahead' ? gitHeadCommit : mappedCommit;
  const checkoutRevisionShort = checkoutRevision.slice(0, 7);
  const gitIsAhead = currentStatus === 'git_ahead';
  const gitAdvancedFromPreview = false;
  const isCheckoutPersona = false;
  const mappedAt = liveMapping?.lastSyncedAt ? new Date(liveMapping.lastSyncedAt).toLocaleString() : app.lastSync;
  const lastModified = liveApp?.attributes?.modifiedDate ? new Date(liveApp.attributes.modifiedDate).toLocaleString() : 'Loading from Qlik…';

  return (
    <section className="app-detail-page">
      <div className="detail-breadcrumb"><Link to="/apps"><ArrowLeftOutlined /> Qlik Apps</Link><span>/</span><strong>{app.name}</strong></div>

      <div className="detail-hero">
        <div className="detail-identity">
          <span className="app-glyph app-glyph-bars"><BarChartOutlined /></span>
          <div>
            <h1>{app.name}</h1>
            <dl className="identity-list">
              <dt>App ID</dt><dd>{app.id}</dd>
              <dt>Owner</dt><dd>Bohua Li</dd>
              <dt>Last modified</dt><dd>{lastModified}</dd>
              <dt>Publish state</dt><dd><span className="status-dot status-neutral" /> Not published</dd>
              <dt>Qlik link</dt><dd><a href={`https://psdemo.eu.qlikcloud.com/sense/app/${app.id}`} target="_blank" rel="noreferrer">Open in Qlik <ExportOutlined /></a></dd>
            </dl>
          </div>
        </div>

        <section className="mapping-card">
          <div className="card-title-row"><h2>GitHub mapping</h2><span className="git-state connected"><span className="status-dot" /> Connected</span></div>
          <dl>
            <dt><GithubOutlined /> Repository</dt><dd>{mappedRepository}</dd><dd className="link-column"><a href="https://github.com/bohua/consumer-sales-demo" target="_blank" rel="noreferrer">View on GitHub <ExportOutlined /></a></dd>
            <dt><BranchesOutlined /> Branch</dt><dd>{mappedBranch}</dd><dd />
            <dt><ClockCircleOutlined /> Last checked</dt><dd>{mappedAt} by Bohua Li</dd><dd />
            <dt><BranchesOutlined /> Git status</dt><dd>{syncSummary?.status === 'git_ahead' ? `${syncSummary.gitChangeCount ?? 1} update${syncSummary.gitChangeCount === 1 ? '' : 's'} ready to pull` : syncSummary?.status === 'qlik_ahead' ? `${syncSummary.qlikChangeCount ?? 1} Qlik update${syncSummary.qlikChangeCount === 1 ? '' : 's'} ready to push` : syncSummary?.status === 'auto_merge' ? 'Qlik and Git both have updates' : syncSummary?.status === 'conflict' ? 'Conflict requires review' : 'Up to date'}</dd><dd className="link-column"><a href="https://github.com/bohua/consumer-sales-demo" target="_blank" rel="noreferrer">View on GitHub <ExportOutlined /></a></dd>
          </dl>
        </section>
      </div>

      <AppSectionTabs appId={app.id} active="overview" previewAppId={preview?.previewAppId} />

      {gitAdvancedFromPreview && <Alert className="pull-required-banner author-handoff-banner" showIcon type="info" message="Reviewed Preview advanced Git · source App intentionally unchanged" description={<span>Persona A checked the reviewed Preview into Git <code>{gitHeadCommitShort}</code>. The persistent source App remains at <code>{mappedCommitShort}</code>, while Persona B receives the pull handoff.</span>} action={<a className="ant-btn ant-btn-primary" href="https://github.com/bohua/consumer-sales-demo" target="_blank" rel="noreferrer">View checked-in revision</a>} />}
      {gitIsAhead && <Alert className="pull-required-banner" showIcon type="warning" message="Pull required - Git has updates for this App" description={<span>Git contains {syncSummary?.gitChangeCount ?? 1} update{syncSummary?.gitChangeCount === 1 ? '' : 's'} that are not in the current Qlik App. Review the business-readable diff before creating a disposable Preview.</span>} action={<Link className="ant-btn ant-btn-primary" to={`/apps/${app.id}/commit`}>Review incoming changes</Link>} />}
      {remediationTestPlan && remediationCase && <Alert className="release-remediation-banner" showIcon type="error" message={<>Release remediation · <code>{remediationObject ?? remediationCase}</code></>} description="A signed Engine-executed test blocked this release. Create a disposable Preview from the approved Git revision, correct the affected Qlik object, review the exact diff, and check in a new revision. The source App remains unchanged until you explicitly commit." action={<div className="remediation-banner-actions"><Link to={`/tests/plans/${remediationTestPlan}/execution?${new URLSearchParams({ ...(remediationRun ? { run: remediationRun } : {}), case: remediationCase }).toString()}`}>Review evidence</Link><a className="ant-btn ant-btn-primary" href="#checkout-workflow">Prepare Preview</a>{remediationReleasePlan && <Link to={`/cd/apps/${remediationReleasePlan}`}>Release plan</Link>}</div>} />}

      <div className="detail-body">
        <aside className="detail-metadata-column">
          <section className="detail-card">
            <h2>App metadata</h2>
            <dl className="compact-metadata">
              <dt>App name</dt><dd>{app.name}</dd>
              <dt>App ID</dt><dd className="break-id">{app.id}</dd>
              <dt>Space</dt><dd>Ziheng</dd>
              <dt>Preview space</dt><dd>CICD Dev</dd>
            </dl>
          </section>
          <section className="detail-card json-project-card">
            <h2>JSON project</h2>
            <dl className="compact-metadata">
              <dt>Exported files</dt><dd>131</dd>
              <dt>Repository</dt><dd>{mappedRepository}</dd>
              <dt>Branch</dt><dd>{mappedBranch}</dd>
              <dt>Status</dt><dd>{currentStatus === 'in_sync' ? 'Up to date' : currentStatus === 'git_ahead' ? 'Git updates available' : currentStatus === 'qlik_ahead' ? 'Qlik updates available' : currentStatus === 'auto_merge' ? 'Qlik and Git updates available' : currentStatus === 'conflict' ? 'Conflict requires review' : 'Checking'}</dd>
            </dl>
          </section>
        </aside>

        <section className="ci-actions-column">
          {preview && (
            <section className="preview-workflow-card">
              <div className="preview-workflow-heading">
                <div><span className="workflow-kicker">ACTIVE PREVIEW</span><h2>{preview.previewAppName}</h2><p>Use this disposable app for the full edit-and-review cycle. Your source app stays untouched.</p></div>
                <span className="preview-live"><span className="status-dot" /> Live in CICD Dev</span>
              </div>
              <div className="preview-steps" aria-label="Preview workflow">
                <div className="is-complete"><span>1</span><strong>Preview created</strong><small>Controlled working copy</small></div>
                <div className="is-current"><span>2</span><strong>Edit in Qlik</strong><small>Make the public sheet private, then edit it under My own</small></div>
                <div className={reviewComplete ? 'is-complete' : ''}><span>3</span><strong>Review changes</strong><small>{reviewComplete ? 'All changes reviewed' : 'Compare this Preview with Git'}</small></div>
                <div className={reviewComplete ? 'is-current' : ''}><span>4</span><strong>Commit to GitHub</strong><small>{reviewComplete ? 'Ready to commit' : 'Locked until review is complete'}</small></div>
              </div>
              <div className="preview-actions">
                <a className="ant-btn ant-btn-primary" href={`https://psdemo.eu.qlikcloud.com/sense/app/${preview.previewAppId}`} target="_blank" rel="noreferrer">Open Preview in Qlik <ExportOutlined /></a>
                <Link className="ant-btn" to={commitPath}><ReloadOutlined /> Refresh review</Link>
                <Button danger loading={deletingPreview} icon={<DeleteOutlined />} onClick={() => void deletePreview()}>Delete Preview</Button>
              </div>
              <Alert showIcon type="info" message="Qlik Cloud editing rule" description="Public sheets cannot be edited directly. Use Make private first, then edit the private sheet under My own." />
              {activeSyncStatus === 'conflict' && <Alert className="checkin-review-gate" showIcon type="error" message="Merge conflict — Commit is locked" description={`${reviewApproval?.conflictCount ?? 0} object conflict${reviewApproval?.conflictCount === 1 ? '' : 's'} must be resolved in Qlik or Git before a fresh review can unlock Commit.`} />}
              {activeSyncStatus === 'auto_merge' && <Alert className="checkin-review-gate" showIcon type="info" message="Auto-merge ready" description={`Commit will preserve ${reviewApproval?.gitChangeCount ?? 0} newer Git change${reviewApproval?.gitChangeCount === 1 ? '' : 's'} and apply ${reviewApproval?.qlikChangeCount ?? 0} reviewed Qlik change${reviewApproval?.qlikChangeCount === 1 ? '' : 's'}.`} />}
              {activeSyncStatus === 'git_ahead' && <Alert className="checkin-review-gate" showIcon type="warning" message="Git is ahead" description="The branch changed after this Preview was created. Click-CI will preserve the newer Git revision instead of overwriting it." />}
              {activeSyncStatus === 'qlik_ahead' && <Alert className="checkin-review-gate" showIcon type="info" message="Qlik updates ready to push" description="Complete the review to commit these Qlik changes to Git." />}
            </section>
          )}

          {!hasCommitChanges && (
            <section className="action-card commit-entry-card">
              <div className="action-card-heading"><div><h2>This App is up to date with Git.</h2><p>No Qlik changes are waiting for review. Use the Qlik link above to open the App, make a controlled change, then return here and refresh status.</p></div></div>
              <div className="changed-files-row"><div><strong>Commit readiness</strong><div className="muted">Current Qlik App matches Git; no commit action is available until a new App change is detected.</div></div></div>
            </section>
          )}

          {hasCommitChanges && <section className="action-card commit-entry-card">
            <div className="action-card-heading"><div><h2>{isCheckoutPersona ? 'Review incoming Git changes' : 'Review & commit'}</h2><p>{isCheckoutPersona ? 'Compare this App with the latest reviewed Git revision before you pull it into a disposable Preview.' : `Compare ${preview ? 'the active Preview App' : 'the current Qlik App'} with Git, resolve conflicts, and create the commit in one controlled workflow.`}</p></div><Link className="ant-btn ant-btn-primary" to={commitPath}>{isCheckoutPersona ? 'Open incoming diff' : reviewComplete ? 'Continue commit' : activeSyncStatus === 'conflict' ? 'Resolve conflicts' : 'Open Commit'}</Link></div>
            <div className="changed-files-row"><div><strong>{isCheckoutPersona ? 'Pull readiness' : 'Commit readiness'}</strong><div className="muted">{liveStateLoading ? 'Checking Qlik and Git for updates...' : isCheckoutPersona ? gitIsAhead ? `${syncSummary?.gitChangeCount ?? 1} Git update${syncSummary?.gitChangeCount === 1 ? ' is' : 's are'} ready to inspect.` : 'This App currently matches Git.' : reviewComplete ? 'Evidence is approved and ready to commit.' : 'Review every detected object before Git can be updated.'}</div></div><Link className="review-link" to={commitPath}>{isCheckoutPersona ? 'Review incoming changes' : reviewComplete ? 'Continue' : 'Review changes'}</Link></div>
            {!preview && activeSyncStatus === 'conflict' && <Alert className="checkin-review-gate" showIcon type="error" message="Merge conflict — Commit is locked" description={`${reviewApproval?.conflictCount ?? 0} object conflict${reviewApproval?.conflictCount === 1 ? '' : 's'} must be resolved in Qlik or Git before a fresh review can unlock Commit.`} />}
            {!preview && activeSyncStatus === 'auto_merge' && <Alert className="checkin-review-gate" showIcon type="info" message="Auto-merge ready" description={`Commit will preserve ${reviewApproval?.gitChangeCount ?? 0} newer Git change${reviewApproval?.gitChangeCount === 1 ? '' : 's'} and apply ${reviewApproval?.qlikChangeCount ?? 0} reviewed Qlik change${reviewApproval?.qlikChangeCount === 1 ? '' : 's'}.`} />}
            {!preview && activeSyncStatus === 'git_ahead' && <Alert className="checkin-review-gate" showIcon type="warning" message="Git is ahead" description="The branch changed after this review. Click-CI will preserve the newer Git revision instead of overwriting it." />}
            {!preview && activeSyncStatus === 'qlik_ahead' && <Alert className="checkin-review-gate" showIcon type="info" message="Qlik updates ready to push" description="The reviewed Qlik changes are ready to commit to Git." />}
            {!isCheckoutPersona && !reviewComplete && activeSyncStatus !== 'conflict' && <Alert className="checkin-review-gate" showIcon type="warning" message="Commit requires review" description={`Open Commit and review every detected change. If the ${preview ? 'Preview' : 'Qlik App'} or Git branch changes afterward, the server will require a fresh review.`} />}
          </section>}

          <section className="action-card checkout-card" id="checkout-workflow">
            <div className="action-card-heading"><div><h2>Check out</h2><p>Restore Git content into a temporary Preview App for controlled validation. This never overwrites the current App.</p></div><Button aria-label="Create Preview App" disabled={Boolean(preview) || (isCheckoutPersona && liveStateLoading)} loading={checkingOut} icon={<CloudDownloadOutlined />} onClick={() => void checkOut()}>{preview ? 'Preview active' : liveStateLoading ? 'Checking Git updates' : gitIsAhead ? 'Check out latest' : 'Check out'}</Button></div>
            <label className="field-label">Git content<Select aria-label="Git content" disabled={liveStateLoading} value={checkoutRevision}><Option value={checkoutRevision}>{gitIsAhead ? 'Latest Git content' : 'Current Git content'}</Option></Select></label>
            <Alert showIcon type="info" message="The current app will not be modified." description="A disposable Preview App will be restored from the selected Git content in CICD Dev." />
            {checkoutRun && <CheckoutExecutionRoadmap run={checkoutRun} />}
          </section>

          {actionMessage && <Alert className="detail-action-result" showIcon type={actionMessage.type} message={actionMessage.text} />}
        </section>
      </div>
    </section>
  );
};
